import { useMutation, useQuery } from "@apollo/client";
import { Typography } from "@mui/material";
import { Grid, Stack } from "@mui/system";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { DataGrid } from "components/DataGrid";
import { TruncateTypography } from "components/TruncateTypography";
import { ProcessedManifestsQuery, WasteLinesUnifiedQuery } from "generated-graphql/graphql";
import { usePaginationModel } from "hooks/usePaginationModel";
import { useParams } from "react-router";
import { formatNumber } from "util/formatNumber";

import { WASTE_LINES_UNIFIED_QUERY } from "../ManifestLineItems/api";
import { DELETE_VENDOR_MANIFEST_AND_LINE_ITEMS_MUTATION } from "./api";

type Manifest = ProcessedManifestsQuery["processedManifests"]["items"][number];
type LineItem = NonNullable<WasteLinesUnifiedQuery["wasteLinesUnified"]>["items"][number];

interface DeleteManifestDialogProps {
  onClose: () => void;
  manifest: Manifest;
  onDeleteSuccess?: () => void;
}

export const DeleteManifestDialog = ({
  onClose,
  manifest,
  onDeleteSuccess,
}: DeleteManifestDialogProps) => {
  const alerts = useAlerts();
  const { tenantId } = useParams<{ tenantId: string }>();
  const [paginationModel, setPaginationModel] = usePaginationModel(5);

  // Both manifest lists, because this dialog opens from either one. Apollo
  // refetches only queries that are currently active, so naming the list the
  // user is not looking at costs nothing. Naming just the V2 query left a
  // deleted manifest on the merged list until the page was reloaded.
  const [deleteManifest, { loading: deletingManifest }] = useMutation(
    DELETE_VENDOR_MANIFEST_AND_LINE_ITEMS_MUTATION,
    {
      refetchQueries: ["ProcessedManifests", "ShippingPaperwork"],
    }
  );

  // Fetch line items for this manifest using server-side pagination
  const {
    data: lineItemsData,
    previousData: prevLineItemsData,
    loading: loadingLineItems,
  } = useQuery(WASTE_LINES_UNIFIED_QUERY, {
    variables: {
      search: `tenantId:${tenantId} processedManifestId:${manifest.id}`,
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
    },
    skip: !tenantId || !manifest.id,
  });

  const lineItems =
    (loadingLineItems
      ? prevLineItemsData?.wasteLinesUnified?.items
      : lineItemsData?.wasteLinesUnified?.items) ?? [];
  const lineItemsCount =
    (loadingLineItems
      ? prevLineItemsData?.wasteLinesUnified?.count
      : lineItemsData?.wasteLinesUnified?.count) ?? 0;

  const handleDelete = async () => {
    try {
      const result = await deleteManifest({
        variables: {
          id: manifest.id,
        },
      });
      if (result.data?.deleteVendorManifestAndLineItems) {
        alerts.success("Manifest successfully deleted");
        onClose();
        onDeleteSuccess?.();
      } else {
        alerts.error("Failed to delete manifest");
      }
    } catch (error) {
      alerts.error("Failed to delete manifest");
      console.error("Error deleting manifest:", error);
    }
  };

  return (
    <ConfirmDialog
      open
      onClose={onClose}
      header="Are you sure you want to delete this vendor manifest?"
      loading={deletingManifest}
      onConfirm={handleDelete}
      confirmText="Yes, delete"
      info={
        <Stack spacing={2}>
          <Grid container columnSpacing={2} rowSpacing={1}>
            <Grid size={3}>
              <Typography variant="caption" color="textSecondary">
                Manifest #
              </Typography>
            </Grid>
            <Grid size={9}>
              <Typography>{manifest.manifestTrackingNumber || "N/A"}</Typography>
            </Grid>
            <Grid size={3}>
              <Typography variant="caption" color="textSecondary">
                Facility Name
              </Typography>
            </Grid>
            <Grid size={9}>
              <Typography>{manifest.facility?.name || "N/A"}</Typography>
            </Grid>
            <Grid size={3}>
              <Typography variant="caption" color="textSecondary">
                EPA ID
              </Typography>
            </Grid>
            <Grid size={9}>
              <Typography>{manifest.epaId || "N/A"}</Typography>
            </Grid>
          </Grid>
          <Typography variant="body1" color="textSecondary">
            Deleting this vendor manifest will also delete all associated line items. This action
            cannot be undone.
          </Typography>
          <DataGrid
            columns={[
              {
                field: "lineItem",
                headerName: "Line Item",
                flex: 1,
                renderCell: (params) => (
                  <TruncateTypography>{params.value || "N/A"}</TruncateTypography>
                ),
              },
              {
                field: "vendorAmount",
                headerName: "Amount (Vendor)",
                flex: 1,
                valueGetter: (_, row) => {
                  const amount = row.vendorAmount;
                  return amount ? `${formatNumber(amount)} lbs` : "";
                },
              },
            ]}
            rows={lineItems}
            loading={loadingLineItems}
            pagination={true}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[5, 10]}
            rowCount={lineItemsCount}
          />
        </Stack>
      }
    />
  );
};
